import { useMemo } from "react";
import {
  CalendarCheck,
  CalendarPlus,
  CaretDown,
  CaretUp,
  X,
  CalendarDots,
  Door,
  WheelchairMotion,
  UsersThree,
  Clock,
  Timer,
  CurrencyCircleDollar,
  CheckCircle,
  Check,
  ClockCountdown,
} from "@phosphor-icons/react";
import "./RoomBar.css";

function RoomBar({
  room,
  todayAppointments = [],
  upcomingAppointments = [],
  onAppointmentClick,
  onOpenCalendar,
  isMinimized = false,
  onToggleMinimize,
}) {
  // Get confirmation status for an appointment
  const getConfirmationStatus = (apt) => {
    if (apt.status === "cancelled") {
      return { text: "בוטלה", type: "cancelled" };
    }
    if (apt.whatsappInitialDeclined === true || apt.whatsappReminderDeclined === true) {
      return { text: "נדחתה", type: "declined" };
    }
    if (apt.whatsappInitialConfirmed === true && apt.whatsappReminderConfirmed === true) {
      return { text: "אושרה", type: "confirmed" };
    }
    if (apt.whatsappReminderConfirmed === true) {
      return { text: "אישור תזכורת", type: "reminder" };
    }
    if (apt.whatsappInitialConfirmed === true) {
      return { text: "אישור ראשוני", type: "initial" };
    }
    if (apt.initialSmsEnabled === false && apt.reminderSmsEnabled === false) {
      return { text: "ללא SMS", type: "nosms" };
    }
    if (!apt.whatsappInitialSent && !apt.whatsappReminderSent && !apt.initialSmsScheduledAt && !apt.reminderSmsScheduledAt) {
      return { text: "ללא SMS", type: "nosms" };
    }
    return { text: "ממתין לאישור", type: "pending" };
  };

  // Format duration
  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMins = Math.round((end - start) / 60000);
    if (diffMins < 60) return `${diffMins} דק׳`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    if (mins === 0) return `${hours} שע׳`;
    return `${hours}:${mins.toString().padStart(2, '0')} שע׳`;
  };

  // Format amount
  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return null;
    return `₪${amount.toLocaleString("he-IL")}`;
  };

  // Today's stats - total and declined (red status only)
  const todayStats = useMemo(() => {
    const total = todayAppointments.length;
    const declined = todayAppointments.filter(
      (apt) =>
        apt.whatsappInitialDeclined === true ||
        apt.whatsappReminderDeclined === true
    ).length;

    return { total, declined };
  }, [todayAppointments]);

  // Upcoming stats
  const upcomingStats = useMemo(() => {
    const total = upcomingAppointments.length;
    const declined = upcomingAppointments.filter(
      (apt) =>
        apt.whatsappInitialDeclined === true ||
        apt.whatsappReminderDeclined === true
    ).length;

    return { total, declined };
  }, [upcomingAppointments]);

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("he-IL", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  };

  // Group upcoming appointments by day
  const groupedUpcoming = useMemo(() => {
    const groups = {};
    upcomingAppointments
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
      .forEach((apt) => {
        const dateKey = new Date(apt.startTime).toLocaleDateString("he-IL");
        if (!groups[dateKey]) {
          groups[dateKey] = {
            dateLabel: formatDate(apt.startTime),
            appointments: [],
          };
        }
        groups[dateKey].appointments.push(apt);
      });
    return Object.values(groups);
  }, [upcomingAppointments]);

  return (
    <div className={`room-bar ${isMinimized ? "minimized" : ""}`}>
      {/* Room Header */}
      <div className="room-bar-header">
        <div className="room-info">
          <div className="room-avatar">
            <Door size={24} weight="fill" />
          </div>
          <div className="room-details">
            <span className="room-name">{room.name}</span>
            <div className="room-badges">
              {room.isAccessible && (
                <span className="room-badge accessible" title="נגיש לנכים">
                  <WheelchairMotion size={12} weight="bold" />
                </span>
              )}
              {room.capacity > 1 && (
                <span className="room-badge capacity" title={`קיבולת: ${room.capacity}`}>
                  <UsersThree size={12} weight="bold" />
                  {room.capacity}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="calendar-view-btn"
            onClick={() => onOpenCalendar(room)}
            title="צפה ביומן שבועי"
          >
            <CalendarDots size={18} weight="fill" />
          </button>
          <button
            className="minimize-btn"
            onClick={() => onToggleMinimize(room.id)}
            title={isMinimized ? "הרחב" : "מזער"}
          >
            {isMinimized ? (
              <CaretDown size={18} weight="bold" />
            ) : (
              <CaretUp size={18} weight="bold" />
            )}
          </button>
        </div>
      </div>

      {/* Content - hidden when minimized */}
      {!isMinimized && (
        <div className="room-bar-content">
          {/* Today Section */}
          <div className="bar-section">
            <div className="bar-section-header">
              <CalendarCheck size={18} weight="fill" />
              <h4>היום</h4>
            </div>

            {/* Today Stats */}
            <div className="bar-stats">
              <div className="bar-stat-item">
                <span className="bar-stat-value">{todayStats.total}</span>
                <span className="bar-stat-label">פגישות</span>
              </div>
              {todayStats.declined > 0 && (
                <div className="bar-stat-item declined">
                  <X size={14} weight="bold" />
                  <span className="bar-stat-value">{todayStats.declined}</span>
                  <span className="bar-stat-label">דחו</span>
                </div>
              )}
            </div>

            {/* Today's Appointments List */}
            <div className="bar-appointments-list">
              {todayAppointments.length === 0 ? (
                <p className="bar-empty-message">אין פגישות להיום</p>
              ) : (
                <div className="bar-apt-cards-list">
                  {todayAppointments
                    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
                    .map((apt) => {
                      const confirmStatus = getConfirmationStatus(apt);
                      const duration = formatDuration(apt.startTime, apt.endTime);
                      const amount = formatAmount(apt.paymentAmount);

                      return (
                        <div
                          key={apt.id}
                          className={`bar-apt-card bar-apt-card-${confirmStatus.type}`}
                          onClick={() => onAppointmentClick(apt)}
                        >
                          <div className="bar-apt-card-header">
                            <span className="bar-apt-card-name">
                              {apt.patientName}
                              {apt.recurringIndex && apt.recurringTotalCount && (
                                <span className="apt-card-series-tag"> ({apt.recurringIndex}/{apt.recurringTotalCount})</span>
                              )}
                            </span>
                          </div>
                          <div className="bar-apt-card-details">
                            <div className="bar-apt-card-detail">
                              <Clock size={11} weight="fill" />
                              <span>{formatTime(apt.startTime)}</span>
                            </div>
                            {duration && (
                              <div className="bar-apt-card-detail">
                                <Timer size={11} weight="fill" />
                                <span>{duration}</span>
                              </div>
                            )}
                            {amount && (
                              <div className="bar-apt-card-detail bar-apt-card-amount">
                                <CurrencyCircleDollar size={11} weight="fill" />
                                <span>{amount}</span>
                              </div>
                            )}
                          </div>
                          <div className="bar-apt-card-footer">
                            <div className={`bar-apt-card-status bar-apt-card-status-${confirmStatus.type}`}>
                              {confirmStatus.type === "confirmed" && <CheckCircle size={11} weight="fill" />}
                              {confirmStatus.type === "initial" && <Check size={11} weight="bold" />}
                              {confirmStatus.type === "reminder" && <CheckCircle size={11} weight="fill" />}
                              {confirmStatus.type === "pending" && <ClockCountdown size={11} weight="fill" />}
                              {confirmStatus.type === "nosms" && <Check size={11} weight="bold" />}
                              {(confirmStatus.type === "declined" || confirmStatus.type === "cancelled") && <X size={11} weight="bold" />}
                              <span>{confirmStatus.text}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Appointments Section */}
          <div className="bar-section upcoming">
            <div className="bar-section-header">
              <CalendarPlus size={18} weight="fill" />
              <h4>פגישות הבאות</h4>
            </div>

            {/* Upcoming Stats */}
            <div className="bar-stats">
              <div className="bar-stat-item">
                <span className="bar-stat-value">{upcomingStats.total}</span>
                <span className="bar-stat-label">פגישות</span>
              </div>
              {upcomingStats.declined > 0 && (
                <div className="bar-stat-item declined">
                  <X size={14} weight="bold" />
                  <span className="bar-stat-value">
                    {upcomingStats.declined}
                  </span>
                  <span className="bar-stat-label">דחו</span>
                </div>
              )}
            </div>

            {/* Upcoming List - grouped by day */}
            {upcomingAppointments.length === 0 ? (
              <p className="bar-empty-message">אין פגישות קרובות</p>
            ) : (
              <div className="bar-appointments-list grouped">
                {groupedUpcoming.map((group) => (
                  <div key={group.dateLabel} className="bar-day-group">
                    <h5 className="bar-day-group-header">{group.dateLabel}</h5>
                    <div className="bar-apt-cards-list">
                      {group.appointments.map((apt) => {
                        const confirmStatus = getConfirmationStatus(apt);
                        const duration = formatDuration(apt.startTime, apt.endTime);
                        const amount = formatAmount(apt.paymentAmount);

                        return (
                          <div
                            key={apt.id}
                            className={`bar-apt-card bar-apt-card-${confirmStatus.type}`}
                            onClick={() => onAppointmentClick(apt)}
                          >
                            <div className="bar-apt-card-header">
                              <span className="bar-apt-card-name">
                                {apt.patientName}
                                {apt.recurringIndex && apt.recurringTotalCount && (
                                  <span className="apt-card-series-tag"> ({apt.recurringIndex}/{apt.recurringTotalCount})</span>
                                )}
                              </span>
                            </div>
                            <div className="bar-apt-card-details">
                              <div className="bar-apt-card-detail">
                                <Clock size={11} weight="fill" />
                                <span>{formatTime(apt.startTime)}</span>
                              </div>
                              {duration && (
                                <div className="bar-apt-card-detail">
                                  <Timer size={11} weight="fill" />
                                  <span>{duration}</span>
                                </div>
                              )}
                              {amount && (
                                <div className="bar-apt-card-detail bar-apt-card-amount">
                                  <CurrencyCircleDollar size={11} weight="fill" />
                                  <span>{amount}</span>
                                </div>
                              )}
                            </div>
                            <div className="bar-apt-card-footer">
                              <div className={`bar-apt-card-status bar-apt-card-status-${confirmStatus.type}`}>
                                {confirmStatus.type === "confirmed" && <CheckCircle size={11} weight="fill" />}
                                {confirmStatus.type === "initial" && <Check size={11} weight="bold" />}
                                {confirmStatus.type === "reminder" && <CheckCircle size={11} weight="fill" />}
                                {confirmStatus.type === "pending" && <ClockCountdown size={11} weight="fill" />}
                                {confirmStatus.type === "nosms" && <Check size={11} weight="bold" />}
                                {(confirmStatus.type === "declined" || confirmStatus.type === "cancelled") && <X size={11} weight="bold" />}
                                <span>{confirmStatus.text}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default RoomBar;
